// Franquia do plano Free — 250 INTERAÇÕES/mês (enviadas + recebidas).
//
// Espelha a regra do backend (internal/entitlement.FreeQuotaExceeded): só o Free
// tem franquia; Pro e cortesia são ILIMITADOS; franquia <= 0 (não configurada)
// nunca conta nem bloqueia. Quando estoura, o ENVIO para (402
// `free_quota_exceeded`) — o RECEBIMENTO continua.
//
// Aqui está só o cálculo puro + o hook de leitura; o visual fica no
// FreeQuotaGauge (medidor) e no FreeQuotaBanner (aviso/bloqueio).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { apiFetch } from './api';
import { useT } from './i18n';

/** A partir deste percentual a conta é avisada de que a franquia está acabando. */
export const FREE_QUOTA_WARN_PCT = 80;

/** Código do 402 que o backend devolve quando a franquia do Free acabou. */
export const FREE_QUOTA_CODE = 'free_quota_exceeded';

/** Recorte de /me/entitlements que importa para a franquia (tudo opcional: a
 *  mesma query key é compartilhada com telas que tipam o payload de outro jeito). */
export interface QuotaEntitlements {
  plan?: string;
  messages_used?: number;
  message_free_count?: number;
}

export interface FreeQuotaState {
  /** A conta está SUJEITA à franquia? (Free com franquia configurada.) Pro e cortesia = false. */
  metered: boolean;
  used: number;
  included: number;
  /** 0–100, já limitado no teto (usado pode passar da franquia em corrida). */
  pct: number;
  /** >= 80% e ainda não bloqueada. */
  warning: boolean;
  /** Franquia esgotada: o envio está pausado. */
  blocked: boolean;
  /**
   * Quando o contador zera. O backend chaveia o contador por mês UTC
   * (`bzapper:usage:msgs:<tenant>:<YYYYMM>`), então a virada é o dia 1º do mês
   * seguinte em UTC — NÃO é a data de renovação da assinatura.
   */
  resetsAt: Date;
}

/** Primeiro instante do próximo mês UTC: é quando o contador do backend zera. */
export function nextMonthlyReset(now: Date = new Date()): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

/**
 * freeQuotaState traduz o payload de entitlements no estado da franquia. Função
 * pura de propósito: é a mesma decisão do backend e precisa ser testável.
 *
 * Payload ausente (carregando/401) = não medida: preferimos não mostrar nada a
 * acusar bloqueio numa conta Pro por causa de um GET que ainda não voltou.
 */
export function freeQuotaState(e: QuotaEntitlements | null | undefined, now?: Date): FreeQuotaState {
  const included = e?.message_free_count ?? 0;
  const used = e?.messages_used ?? 0;
  const plan = e?.plan ?? '';
  const metered = !!e && plan !== 'pro' && plan !== 'exempt' && included > 0;
  const pct = metered ? Math.min(100, Math.round((used / included) * 100)) : 0;
  const blocked = metered && used >= included;
  return {
    metered,
    used,
    included,
    pct,
    warning: metered && !blocked && pct >= FREE_QUOTA_WARN_PCT,
    blocked,
    resetsAt: nextMonthlyReset(now),
  };
}

/**
 * useFreeQuota lê a franquia da conta. Usa a MESMA query key `['entitlements']`
 * das telas de Plano/Cobrança/Campanhas, então não gera requisição extra —
 * todas batem em /me/entitlements.
 */
export function useFreeQuota(): FreeQuotaState & { loading: boolean } {
  const q = useQuery({
    queryKey: ['entitlements'],
    retry: false,
    queryFn: async (): Promise<QuotaEntitlements | null> => {
      const r = await apiFetch('GET', '/me/entitlements');
      return r.ok ? (r.data as QuotaEntitlements) : null;
    },
  });
  return { ...freeQuotaState(q.data), loading: q.isLoading };
}

/**
 * freeQuotaError extrai a mensagem do 402 `free_quota_exceeded`. A mensagem JÁ
 * vem traduzida pelo servidor (o painel não reescreve: o texto do servidor é o
 * mesmo que o cliente vê na API).
 */
export function freeQuotaError(result: { status: number; data: unknown }): string | null {
  if (result.status !== 402) return null;
  const body = result.data as { code?: string; message?: string } | null;
  if (body?.code !== FREE_QUOTA_CODE) return null;
  return body.message ?? '';
}

/**
 * useFreeQuotaToast devolve o tratador do 402 da franquia para quem ENVIA
 * (composer do Inbox, Playground, início de campanha).
 *
 * Devolve `true` quando ELE cuidou do erro — aí quem chamou não deve mostrar
 * mais nada. Em vez de um toast de erro cru, mostra a mensagem do servidor (já
 * traduzida) com o botão de upgrade ao lado, e refaz a leitura dos
 * entitlements para o medidor e o banner aparecerem na hora.
 */
export function useFreeQuotaToast() {
  const t = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  return (result: { status: number; data: unknown }): boolean => {
    const msg = freeQuotaError(result);
    if (msg === null) return false;
    void qc.invalidateQueries({ queryKey: ['entitlements'] });
    toast.error(msg || t('freeQuota.blockedTitle'), {
      duration: 12_000,
      action: { label: t('freeQuota.upgrade'), onClick: () => navigate('/billing') },
    });
    return true;
  };
}
